import { Fetcher } from '@this/src/util';
import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Link } from '@this/shared/ui/navigations/link';
import { Input } from '@this/shared/ui/inputs/input';
import { Button } from '@this/shared/ui/inputs/button';
import { Alert } from '@this/shared/ui/feedbacks/alert';
import type { KnowledgeResponseArgs } from '@this/src/domain/knowledge';
import Knowledge, { convertKnowledgeResponseToArgs } from '@this/src/domain/knowledge';
import { Box, IconButton, InputAdornment } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { createTheme } from '@this/constants/themes';
import type { SigninFormData } from './types';
import type { SignInResponse } from './user_signin_blue';
import { Modal, ModalBody, ModalHeader } from '../shared/ui/feedbacks/modal';
import UserPasswordResetBlueForm from './user_password_reset_blue_form';

interface Props {
  signin: ({ email, password }: { email: string; password: string }) => Promise<SignInResponse>;
  resetPassword: (email: string) => Promise<boolean>;
  serviceId: number;
  isPasswordExpired: boolean;
}

interface KnowledgeResponse {
  knowledges: KnowledgeResponseArgs[];
}

const UserSigninBlueForm: React.FC<Props> = observer(({ signin, resetPassword, serviceId, isPasswordExpired }) => {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [errorText, setErrorText] = useState<SignInResponse>(null);
  const [knowledges, setKnowledges] = useState<Knowledge[]>([]);
  const [showKnowledges, setShowKnowledges] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isShowSigninForm, setIsShowSigninForm] = useState<boolean>(!isPasswordExpired);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // テーマを取得
  const theme = createTheme(serviceId);

  // urlからログインを失敗したという情報を渡して初回アクセス時にその情報を反映できる機構を入れる。
  // SSOのIDPInitiatedでアクセスがあったが、AIトラベル上で、ユーザ登録されていない
  // もしくはログイン方法がパスワードになっている場合に使われることを想定している。
  const [errorTypeFromUrl] = useState<string | undefined>(utils.getParam('error_type'));

  const fetchKnowledges = useCallback(() => {
    Fetcher.get<KnowledgeResponse>('/knowledges?show_login=true').then(res => {
      setKnowledges(res.knowledges.map(raw => new Knowledge(convertKnowledgeResponseToArgs(raw))));
    });
  }, []);

  useEffect(() => {
    fetchKnowledges();
  }, [fetchKnowledges]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const form: SigninFormData = {
        email,
        password
      };
      const result = await signin(form);
      setErrorText(result);
    } finally {
      setIsLoading(false);
    }
  };

  const adornment = () => {
    return (
      <InputAdornment position="end">
        <IconButton
          aria-label={showPassword ? 'hide the password' : 'display the password'}
          onClick={handleClickShowPassword}
          onMouseDown={handleMouseDownPassword}
          onMouseUp={handleMouseUpPassword}
          edge="end"
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    );
  };

  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const navigateToPasswordReset = () => {
    setIsShowSigninForm(false);
  };

  const navigateToSignin = () => {
    setIsShowSigninForm(true);
  };

  if (!isShowSigninForm) {
    return (
      <UserPasswordResetBlueForm
        isPasswordExpired={isPasswordExpired}
        navigateToBack={navigateToSignin}
        resetPassword={resetPassword}
        serviceId={serviceId}
      />
    );
  }

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: '19px', alignItems: 'flex-start', width: '100%' }}
    >
      {/* ロゴ */}
      <div style={{ height: '36.585px', width: '225.769px', position: 'relative', flexShrink: 0 }}>
        <img
          alt={theme.serviceName}
          src={theme.signInBodyLogoPath}
          style={{
            position: 'absolute',
            inset: 0,
            maxWidth: 'none',
            objectFit: 'cover',
            objectPosition: '50% 50%',
            pointerEvents: 'none',
            width: '100%',
            height: '100%'
          }}
        />
      </div>

      {/* タイトル（管理者・手配者ログインの場合のみ表示） */}
      {location.pathname === '/admins/sign_in' && (
        <div
          style={{
            fontFamily: "'Inter', 'Noto Sans JP', sans-serif",
            fontWeight: 500,
            fontSize: '16px',
            color: '#555555',
            marginBottom: '8px'
          }}
        >
          管理者ログイン
        </div>
      )}
      {location.pathname === '/arrangers/sign_in' && (
        <div
          style={{
            fontFamily: "'Inter', 'Noto Sans JP', sans-serif",
            fontWeight: 500,
            fontSize: '16px',
            color: '#555555',
            marginBottom: '8px'
          }}
        >
          手配者ログイン
        </div>
      )}

      {/* エラーメッセージ */}
      {errorText === 'LoginFailed' && (
        <Alert variant="danger" style={{ marginBottom: '16px', width: '100%' }}>
          ログインに失敗しました。
          <br />
          メールアドレス・パスワードを
          <br />
          ご確認ください。
        </Alert>
      )}
      {errorText === 'AccountLocked' && (
        <Alert variant="danger" style={{ marginBottom: '16px', width: '100%' }}>
          アカウントがロックされました。
          <br />
          30分後以降に再度ログインをお試しください。
        </Alert>
      )}
      {errorTypeFromUrl === 'NotRegisteredIDP' && (
        <Alert variant="danger" style={{ marginBottom: '16px', width: '100%' }}>
          ログインに失敗しました。
          <br />
          登録されていないユーザです。
        </Alert>
      )}
      {location.search === '?token=expired' && (
        <Alert variant="danger" style={{ marginBottom: '16px', width: '100%' }}>
          このURLは無効です。
          <br />
          既に登録がお済みの方は<a href="/users/sign_in">コチラ</a>から <br />
          ログインしてください。
          <br />
          登録がまだお済みで無い方はお手数ですが <br />
          一度貴社管理者の方にお問合せください。
        </Alert>
      )}

      {/* メールアドレスラベル */}
      <div
        style={{
          fontFamily: "'Inter', 'Noto Sans JP', sans-serif",
          fontWeight: 500,
          fontSize: '14px',
          color: '#555555',
          width: '100%'
        }}
      >
        メールアドレス
      </div>

      {/* フォーム */}
      <form
        name="signinForm"
        action="/users/sign_in"
        method="post"
        onSubmit={handleSubmit}
        style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '19px' }}
      >
        <input type="hidden" name="user[service_id]" value={serviceId} />

        {/* メールアドレス入力 */}
        <div style={{ height: '48.474px', width: '100%', position: 'relative' }}>
          <Input
            type="email"
            placeholder=""
            name="user[email]"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            fullWidth
            style={{
              height: '100%',
              border: '1px solid #0080cb'
            }}
          />
        </div>

        {/* パスワードラベル */}
        <div
          style={{
            fontFamily: "'Inter', 'Noto Sans JP', sans-serif",
            fontWeight: 500,
            fontSize: '14px',
            color: '#555555',
            width: '100%'
          }}
        >
          パスワード
        </div>

        {/* パスワード入力 */}
        <div style={{ height: '48.474px', width: '100%', position: 'relative' }}>
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder=""
            name="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            endAdornment={adornment()}
            fullWidth
            style={{
              height: '100%',
              border: '1px solid #0080cb'
            }}
          />
        </div>

        {/* SAML認証の説明文 */}
        {location.pathname === '/users/sign_in' && (
          <div
            style={{
              fontFamily: "'Inter', 'Noto Sans JP', sans-serif",
              fontWeight: 400,
              fontSize: '15px',
              lineHeight: '25px',
              color: '#555555',
              width: '100%'
            }}
          >
            SAML認証をご利用のお客様は、メールアドレスのみ入力してログインボタンをクリックしてください
          </div>
        )}

        {/* ログインボタン */}
        <Button
          type="submit"
          disabled={email.length === 0}
          loading={isLoading}
          fullWidth
          style={{ height: '48.474px' }}
        >
          ログイン
        </Button>
      </form>

      {/* リンク群 */}
      <div
        style={{
          fontFamily: "'Inter', 'Noto Sans JP', sans-serif",
          fontWeight: 400,
          fontSize: '15px',
          lineHeight: '40px',
          color: '#0080cb',
          width: '100%'
        }}
      >
        <Link
          size="small"
          onClick={navigateToPasswordReset}
          style={{ fontSize: '15px', display: 'block', marginBottom: '10px' }}
        >
          パスワードをお忘れですか？
        </Link>

        {serviceId === 1 && (
          <>
            <Link
              style={{ fontSize: '15px', display: 'block', marginBottom: '10px' }}
              onClick={() => setShowKnowledges(true)}
            >
              ログインでお困りの方はこちら
            </Link>
            <Modal size="medium" open={showKnowledges} onClose={() => setShowKnowledges(false)}>
              <ModalHeader>ログインでお困りの方へ</ModalHeader>
              <ModalBody>
                <Box fontWeight="bold" marginBottom="10px">
                  よくあるご質問
                </Box>
                <ul style={{ listStyleType: 'disc', marginLeft: '20px' }}>
                  {knowledges.map(knowledge => (
                    <li key={knowledge.salesforceId} style={{ fontSize: '15px', marginBottom: '5px' }}>
                      <Link href={`/knowledges/${knowledge.salesforceId}`} target="_blank">
                        {knowledge.title}
                      </Link>
                    </li>
                  ))}
                </ul>
                <Box marginTop="20px">
                  上記で解決しない場合、
                  <Link href="/inquiries/new" target="_blank">
                    お問い合わせフォーム
                  </Link>
                  よりご連絡ください。
                </Box>
              </ModalBody>
            </Modal>
          </>
        )}

        <Link
          style={{ fontSize: '15px', display: 'block' }}
          href="https://aitravel.cloud/lp/dl"
          isExternal
          target="_blank"
          rel="noopener noreferrer"
        >
          導入をご検討中の企業様はこちら
        </Link>
      </div>
    </div>
  );
});

export default UserSigninBlueForm;
